import type { IAgoraRTCClient } from 'agora-rtc-react';
import type { NetworkQuality } from 'agora-rtc-sdk-ng';
import { sendToDatadog, StudioLogger } from '@studio/logger';
import type {
  AttendeeMetricsData,
  MeetingQuality,
  VideoQualityScore,
  NetworkQualityResult,
  QualityStatus
} from './interfaces';
import {
  DEFAULT_PROCESSING_INTERVAL_MS,
  QUALITY_LOG_SAMPLE_COUNT,
  DEFAULT_EXPECTED_RESOLUTION_HEIGHT,
  OFFSTAGE_MAX_EXPECTED_RESOLUTION_HEIGHT,
  FALLBACK_RESOLUTION_HEIGHT,
  MIN_GOOD_RESOLUTION_HEIGHT,
  MOS_EXCELLENT_THRESHOLD,
  MOS_GOOD_THRESHOLD,
  QUALITY_SCORE_EXCELLENT,
  QUALITY_SCORE_GOOD,
  QUALITY_SCORE_POOR,
  FPS_ONSTAGE_EXCELLENT_THRESHOLD,
  FPS_ONSTAGE_GOOD_THRESHOLD,
  FPS_OFFSTAGE_EXCELLENT_THRESHOLD,
  FPS_OFFSTAGE_GOOD_THRESHOLD,
  AGORA_NETWORK_QUALITY_TO_STRING_MAP,
  SCORE_QUALITY_MAP,
  DEFAULT_CODEC,
  MOS_BASE_R_FACTOR,
  MOS_HIGH_LATENCY_THRESHOLD,
  MOS_LATENCY_FACTOR_NORMAL,
  MOS_LATENCY_FACTOR_HIGH,
  MOS_HIGH_LATENCY_PENALTY,
  MOS_PACKET_LOSS_MULTIPLIER,
  MOS_JITTER_MULTIPLIER,
  MOS_BASE_LATENCY_OFFSET,
  MOS_CALCULATION_CONSTANT_A,
  MOS_CALCULATION_CONSTANT_B,
  MOS_CALCULATION_CONSTANT_C,
  MOS_CALCULATION_R_FACTOR_OFFSET,
  MOS_CALCULATION_MAX_R_FACTOR
} from './constants';

const LOG = new StudioLogger('AgoraMetricsProcessor');

const createDefaultMeetingQuality = () => ({
  audio: {
    available: false,
    score: {
      min: 0,
      avg: 0,
      max: 0
    },
    dataSets: [] as string[]
  },
  downVideo: {
    onStage: {
      available: false,
      videoScore: {
        min: 0,
        avg: 0,
        max: 0
      },
      fpsScore: {
        min: 0,
        avg: 0,
        max: 0
      },
      resolutionScore: {
        min: 0,
        avg: 0,
        max: 0
      },
      receivingMosScore: {
        min: 0,
        avg: 0,
        max: 0
      },
      senderMosScore: {
        min: 0,
        avg: 0,
        max: 0
      },
      dataSets: [] as string[]
    },
    offStage: {
      available: false,
      videoScore: {
        min: 0,
        avg: 0,
        max: 0
      },
      fpsScore: {
        min: 0,
        avg: 0,
        max: 0
      },
      resolutionScore: {
        min: 0,
        avg: 0,
        max: 0
      },
      receivingMosScore: {
        min: 0,
        avg: 0,
        max: 0
      },
      senderMosScore: {
        min: 0,
        avg: 0,
        max: 0
      },
      dataSets: [] as string[]
    }
  },
  upVideo: {
    available: false,
    onStage: false,
    videoScore: {
      min: 0,
      avg: 0,
      max: 0
    },
    fpsScore: {
      min: 0,
      avg: 0,
      max: 0
    },
    resolutionScore: {
      min: 0,
      avg: 0,
      max: 0
    },
    receivingMosScore: {
      min: 0,
      avg: 0,
      max: 0
    },
    senderMosScore: {
      min: 0,
      avg: 0,
      max: 0
    },
    dataSets: [] as string[]
  }
});

class AttendeeMetrics {
  private metrics: AttendeeMetricsData = {};

  updateMetric<K extends keyof AttendeeMetricsData>(metricName: K, value: AttendeeMetricsData[K]): void {
    this.metrics[metricName] = value;
  }

  getMetric<K extends keyof AttendeeMetricsData>(metricName: K): AttendeeMetricsData[K] | undefined {
    return this.metrics[metricName];
  }
}

/**
 * Studio-wide metrics storage for all attendees
 */
class StudioMetrics {
  private studioMetrics: { [attendeeId: string]: AttendeeMetrics } = {};

  getAttendeeMetrics(attendeeId: string): AttendeeMetrics {
    if (!this.studioMetrics[attendeeId]) {
      this.studioMetrics[attendeeId] = new AttendeeMetrics();
    }
    return this.studioMetrics[attendeeId]!;
  }

  updateAttendeeMetrics(attendeeId: string, metrics: AttendeeMetrics): void {
    this.studioMetrics[attendeeId] = metrics;
  }

  getAllMetrics(): { [attendeeId: string]: AttendeeMetrics } {
    return this.studioMetrics;
  }

  removeAttendee(attendeeId: string): void {
    delete this.studioMetrics[attendeeId];
  }

  getAttendeeCount(): number {
    return Object.keys(this.studioMetrics).length;
  }
}

export class AgoraMetricsProcessor {
  private static instance: AgoraMetricsProcessor | null = null;
  private client!: IAgoraRTCClient;
  private currentAttendeeId!: string;
  private studioMetrics!: StudioMetrics;
  private intervalMs: number = DEFAULT_PROCESSING_INTERVAL_MS;
  private processingInterval: NodeJS.Timeout | null = null;
  private onMetricsUpdate: () => void = () => {};

  // Quality tracking arrays
  private audioQuality: string[] = [];
  private videoQuality: Record<string, string[]> = {};
  private meetingQuality: MeetingQuality = createDefaultMeetingQuality();
  private remoteAttendeeNetworkHealth: Record<string, any> = {};
  private onstageAttendees: Record<string, any> = {};
  private downVideoOnStageCount = 0;
  private downVideoOffStageCount = 0;
  private upVideoCount = 0;

  /**
   * Private constructor (Singleton)
   */
  private constructor(client: IAgoraRTCClient, currentAttendeeId: string, onMetricsUpdate: () => void = () => {}) {
    this.client = client;
    this.currentAttendeeId = currentAttendeeId;
    this.studioMetrics = new StudioMetrics();
    this.onMetricsUpdate = onMetricsUpdate;
    this.client.on('network-quality', this.handleNetworkQuality);
    this.startProcessing();
  }

  /**
   * Access point for Singleton
   */
  public static getInstance(
    client?: IAgoraRTCClient,
    currentAttendeeId?: string,
    onMetricsUpdate: () => void = () => {}
  ): AgoraMetricsProcessor {
    if (!AgoraMetricsProcessor.instance) {
      if (!client || !currentAttendeeId) {
        throw new Error('AgoraMetricsProcessor: client and currentAttendeeId are required for first initialization');
      }
      AgoraMetricsProcessor.instance = new AgoraMetricsProcessor(client, currentAttendeeId, onMetricsUpdate);
    }
    return AgoraMetricsProcessor.instance;
  }

  /**
   * Handler for network quality changes
   */
  private handleNetworkQuality = (data: NetworkQuality): void => {
    const attendeeMetrics = this.studioMetrics.getAttendeeMetrics(this.currentAttendeeId);
    attendeeMetrics.updateMetric('networkQuality', AGORA_NETWORK_QUALITY_TO_STRING_MAP[data.uplinkNetworkQuality]);
  };

  private startProcessing(): void {
    this.processingInterval = setInterval(() => {
      this.processMetrics();
    }, this.intervalMs);
  }

  private processMetrics(): void {
    this.updateQualityTracking();
    this.processLocalAttendeeMetrics();
    this.processRemoteAttendeesMetrics();
    this.onMetricsUpdate();
  }

  private updateQualityTracking(): void {
    try {
      // Get local stats
      const audioStats = this.client.getLocalAudioStats();
      const videoStats = this.client.getLocalVideoStats();
      // Process audio quality data
      const audioDataSet = this.getAudioQualityData(audioStats);
      this.audioQuality.push(audioDataSet);

      // Process video quality data for current user (upstream) - only if video stats exist and video is active
      if (videoStats && this.isLocalVideoActive(videoStats)) {
        const currentVideoDataSet = this.getUpstreamVideoQualityData(this.currentAttendeeId, videoStats);
        if (!this.videoQuality[this.currentAttendeeId]) {
          this.videoQuality[this.currentAttendeeId] = [];
        }
        this.videoQuality[this.currentAttendeeId]!.push(currentVideoDataSet);
      }

      // Process remote attendees' video (downstream)
      const remoteUsers = this.client.remoteUsers;
      for (const remoteUser of remoteUsers) {
        const remoteAttendeeId = remoteUser.uid.toString();
        if (remoteAttendeeId !== this.currentAttendeeId && remoteUser.hasVideo) {
          const remoteVideoStats = this.client.getRemoteVideoStats()[remoteAttendeeId];
          if (remoteVideoStats && this.isRemoteVideoActive(remoteVideoStats)) {
            const remoteVideoDataSet = this.getVideoQualityData(remoteAttendeeId, remoteVideoStats);
            if (!this.videoQuality[remoteAttendeeId]) {
              this.videoQuality[remoteAttendeeId] = [];
            }
            this.videoQuality[remoteAttendeeId]?.push(remoteVideoDataSet);
          }
        }
      }

      // Log quality metrics every 10 samples
      if (this.audioQuality.length >= QUALITY_LOG_SAMPLE_COUNT) {
        this.logQualityMetrics();
        this.resetQualityTracking();
      }
    } catch (error) {
      LOG.error('Error updating quality tracking:', error);
    }
  }

  private getAudioQualityData(audioMetrics: any): string {
    const audioQualityData = [];

    const audioPacketLossPercent = audioMetrics?.currentPacketLossRate || 0;
    const audioJitter = audioMetrics?.sendJitterMs || 0;
    const audioRTT = audioMetrics?.sendRttMs || 0;
    const audioMOS = this.computeMOS(audioPacketLossPercent, audioJitter, audioRTT);

    // Receiver-side (down-link) audio MOS
    audioQualityData.push(`rmos:${audioMOS}`);

    const audioScores = [];
    const audioMOSBasedScore = this.getMOSBasedQualityScore(audioMOS);
    audioScores.push(audioMOSBasedScore);

    // Add sender MOS for onstage attendees if available
    const onstageAttendeeIds = this.getOnstageAttendeeIds();
    for (const onstageAttendeeId of onstageAttendeeIds) {
      if (Object.prototype.hasOwnProperty.call(this.remoteAttendeeNetworkHealth, onstageAttendeeId)) {
        const remoteAttendeeAudioMOS = this.remoteAttendeeNetworkHealth[onstageAttendeeId]['audioMOS'] || audioMOS;
        audioQualityData.push(`smos:${remoteAttendeeAudioMOS}`);
        audioScores.push(this.getMOSBasedQualityScore(remoteAttendeeAudioMOS));
      }
    }

    this.meetingQuality.audio.available = true;
    const audioQualityScore = Math.min(...audioScores);

    if (this.meetingQuality.audio.score.min === 0 || this.meetingQuality.audio.score.min > audioQualityScore) {
      this.meetingQuality.audio.score.min = audioQualityScore;
    }

    if (this.meetingQuality.audio.score.max === 0 || this.meetingQuality.audio.score.max < audioQualityScore) {
      this.meetingQuality.audio.score.max = audioQualityScore;
    }

    this.meetingQuality.audio.score.avg += audioQualityScore;
    audioQualityData.push(`fs:${audioQualityScore}`);

    const dataSet = audioQualityData.join('::');
    this.meetingQuality.audio.dataSets.push(dataSet);

    return dataSet;
  }

  private getVideoQualityData(attendeeId: string, videoMetrics: any): string {
    const videoQualityData = [];
    const isOnStage = this.isAttendeeOnStage(attendeeId) ? 1 : 0;

    let meetingQualityType;
    if (isOnStage === 1) {
      this.downVideoOnStageCount += 1;
      meetingQualityType = this.meetingQuality.downVideo.onStage;
    } else {
      this.downVideoOffStageCount += 1;
      meetingQualityType = this.meetingQuality.downVideo.offStage;
    }

    meetingQualityType.available = true;

    videoQualityData.push(`n:${this.getNumMeetingParticipants()}`);
    videoQualityData.push(`os:${isOnStage}`);
    videoQualityData.push(`pr:${videoMetrics.receivePackets || 0}`);

    if ((videoMetrics.receivePackets || 0) === 0) {
      this.processMeetingQuality(meetingQualityType, {
        videoScore: 1,
        fpsScore: 1,
        resolutionScore: 1,
        receivingMosScore: 1,
        senderMosScore: 1
      });
      videoQualityData.push('fs:1');
    } else {
      const videoPacketLossPercent = videoMetrics.packetLossRate || 0;
      const videoJitter = videoMetrics.jitterMs || 0;
      const videoRTT = videoMetrics.end2EndDelay || 0;
      const videoMOS = this.computeMOS(videoPacketLossPercent, videoJitter, videoRTT);
      let senderMOS = 'NA';
      if (Object.prototype.hasOwnProperty.call(this.remoteAttendeeNetworkHealth, attendeeId)) {
        senderMOS = this.remoteAttendeeNetworkHealth[attendeeId]['videoMOS'] || 'NA';
      }

      const videoQualityScore = this.getVideoQualityScore(
        attendeeId,
        videoMOS,
        senderMOS,
        videoMetrics.receiveFrameRate || 0,
        videoMetrics.receiveResolutionHeight || 0
      );

      videoQualityData.push(`fh:${videoMetrics.receiveResolutionHeight || 0}`);
      videoQualityData.push(`efh:${this.getExpectedResolutionHeight(isOnStage === 0)}`);
      videoQualityData.push(`fps:${videoMetrics.receiveFrameRate || 0}`);
      videoQualityData.push(`smos:${senderMOS}`);
      videoQualityData.push(`rmos:${videoMOS}`);
      videoQualityData.push(`fs:${videoQualityScore.videoScore}`);
      this.processMeetingQuality(meetingQualityType, videoQualityScore);
    }

    const dataSet = videoQualityData.join('::');

    if (isOnStage === 1) {
      this.meetingQuality.downVideo.onStage.dataSets.push(dataSet);
    } else {
      this.meetingQuality.downVideo.offStage.dataSets.push(dataSet);
    }

    return dataSet;
  }

  private getUpstreamVideoQualityData(attendeeId: string, videoMetrics: any): string {
    const videoQualityData = [];
    const isOnStage = this.isAttendeeOnStage(attendeeId) ? 1 : 0;

    this.upVideoCount += 1;
    this.meetingQuality.upVideo.available = true;

    videoQualityData.push(`n:${this.getNumMeetingParticipants()}`);
    videoQualityData.push(`os:${isOnStage}`);

    if (isOnStage === 1) {
      this.meetingQuality.upVideo.onStage = true;
    }

    videoQualityData.push(`ps:${videoMetrics?.sendPackets || 0}`);

    if ((videoMetrics?.sendPackets || 0) === 0) {
      this.processMeetingQuality(this.meetingQuality.upVideo, {
        videoScore: 1,
        fpsScore: 1,
        resolutionScore: 1,
        receivingMosScore: 1,
        senderMosScore: 1
      });
      videoQualityData.push('fs:1');
    } else {
      const videoPacketLossPercent = videoMetrics?.currentPacketLossRate || 0;
      const videoJitter = videoMetrics?.sendJitterMs || 0;
      const videoRTT = videoMetrics?.sendRttMs || 0;
      const videoMOS = this.computeMOS(videoPacketLossPercent, videoJitter, videoRTT);

      const videoQualityScore = this.getVideoQualityScore(
        attendeeId,
        videoMOS,
        'NA',
        videoMetrics?.sendFrameRate || 0,
        videoMetrics?.sendResolutionHeight || 0
      );

      videoQualityData.push(`fh:${videoMetrics?.sendResolutionHeight || 0}`);
      videoQualityData.push(`efh:${this.getExpectedResolutionHeight(isOnStage === 0)}`);
      videoQualityData.push(`fps:${videoMetrics?.sendFrameRate || 0}`);
      videoQualityData.push(`rmos:${videoMOS}`);
      videoQualityData.push(`fs:${videoQualityScore.videoScore}`);

      this.processMeetingQuality(this.meetingQuality.upVideo, videoQualityScore);
    }

    const dataSet = videoQualityData.join('::');
    this.meetingQuality.upVideo.dataSets.push(dataSet);

    return dataSet;
  }

  private logQualityMetrics(): void {
    try {
      // Calculate averages for audio
      if (this.audioQuality.length > 0) {
        this.meetingQuality.audio.score.avg =
          Math.round((this.meetingQuality.audio.score.avg / this.audioQuality.length) * 100) / 100;
      }

      // Calculate averages for video streams
      this.calculateVideoScoreAvgs(this.meetingQuality.downVideo.onStage, this.downVideoOnStageCount);
      this.calculateVideoScoreAvgs(this.meetingQuality.downVideo.offStage, this.downVideoOffStageCount);
      this.calculateVideoScoreAvgs(this.meetingQuality.upVideo, this.upVideoCount);
      const networkHealthData = this.collectNetworkHealthDataForAllUsers();
      const numParticipants = this.getNumMeetingParticipants();

      // Log network health data only for current user
      const attendeeData = networkHealthData[this.currentAttendeeId];

      const networkHealthMetrics = {
        networkQuality: attendeeData.networkQuality,
        videoMOS: attendeeData.videoMOS,
        expectedResolutionHeight: attendeeData.expectedResolutionHeight
      };

      // Create metrics object for dashboard compatibility from networkHealthData
      const metrics = {
        // Audio Upstream Metrics
        audioUpstreamLevel: attendeeData.audioUpstreamLevel,
        audioPacketsSent: attendeeData.audioPacketsSent,
        audioUpstreamJitterMs: attendeeData.audioJitter,
        audioPacketLossPercent: attendeeData.audioPacketLoss,
        audioUpstreamRoundTripTimeMs: attendeeData.audioLatency,

        // Audio Downstream Metrics
        audioDownstreamLevel: attendeeData.audioDownstreamLevel,
        audioDownstreamJitterMs: attendeeData.audioDownstreamJitterMs,
        audioPacketsReceived: attendeeData.audioPacketsReceived,
        audioPacketsReceivedFractionLoss: attendeeData.audioPacketsReceivedFractionLoss,

        // Network Metrics
        currentRoundTripTimeMs: attendeeData.audioLatency || attendeeData.videoLatency,
        availableIncomingBitrate: attendeeData.availableIncomingBitrate,
        availableOutgoingBitrate: attendeeData.availableOutgoingBitrate,

        // Page/UI Metrics
        isPageHidden: attendeeData.isPageHidden,

        // Video Frame Metrics
        videoUpstreamFrameHeight: attendeeData.videoUpstreamFrameHeight,
        videoUpstreamFrameWidth: attendeeData.videoUpstreamFrameWidth,
        mediaSourceHeight: attendeeData.mediaSourceHeight,
        mediaSourceWidth: attendeeData.mediaSourceWidth,
        mediaSourceFramesPerSecond: attendeeData.mediaSourceFramesPerSecond,
        videoUpstreamFramesEncodedPerSecond: attendeeData.videoUpstreamFramesEncodedPerSecond,

        // Video Bitrate Metrics
        videoUpstreamBitrate: attendeeData.videoUpstreamBitrate,
        videoDownstreamBitrate: attendeeData.videoDownstreamBitrate,

        // Video Packet Metrics
        videoPacketReceivedPerSecond: attendeeData.videoPacketReceivedPerSecond,
        videoPacketSentPerSecond: attendeeData.videoPacketSentPerSecond,
        videoUpstreamPacketsSent: attendeeData.videoUpstreamPacketsSent,
        videoUpstreamPacketLossPercent: attendeeData.videoPacketLoss,
        nackCountReceivedPerSecond: attendeeData.nackCountReceivedPerSecond,

        // Video Codec/Quality Metrics
        codec: attendeeData.codec,
        videoDownstreamTotalDecodeTimePerSecond: attendeeData.videoDownstreamTotalDecodeTimePerSecond,

        // Video RTT/Jitter Metrics
        videoUpstreamJitterMs: attendeeData.videoJitter,
        videoUpstreamRoundTripTimeMs: attendeeData.videoLatency,

        // Participant Metrics
        numParticipants,
        numVideos: attendeeData.numVideos
      };

      sendToDatadog(
        'AgoraMetricsProcessor',
        {
          networkHealthMetrics,
          metrics,
          meetingQuality: this.meetingQuality,
          audio: this.audioQuality,
          video: this.videoQuality
        },
        `Agora quality logs for ${this.currentAttendeeId}`
      );

      // Reset for next cycle
      this.resetQualityTracking();
    } catch (error) {
      LOG.error('Failed to log quality metrics', error);
    }
  }

  private resetQualityTracking(): void {
    this.audioQuality = [];
    this.videoQuality = {};
    this.meetingQuality = createDefaultMeetingQuality();
    this.downVideoOnStageCount = 0;
    this.downVideoOffStageCount = 0;
    this.upVideoCount = 0;
  }

  private getMOSBasedQualityScore(MOS: number): number {
    let MOSBasedScore = QUALITY_SCORE_POOR;
    if (MOS >= MOS_EXCELLENT_THRESHOLD) {
      MOSBasedScore = QUALITY_SCORE_EXCELLENT;
    } else if (MOS >= MOS_GOOD_THRESHOLD) {
      MOSBasedScore = QUALITY_SCORE_GOOD;
    }
    return MOSBasedScore;
  }

  private getOnstageAttendeeIds(): string[] {
    return Object.keys(this.onstageAttendees);
  }

  private isAttendeeOnStage(attendeeId: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.onstageAttendees, attendeeId);
  }

  private getNumMeetingParticipants(): number {
    return this.studioMetrics.getAttendeeCount();
  }

  private getExpectedResolutionHeight(isOffStage: boolean = false): number {
    return isOffStage ? OFFSTAGE_MAX_EXPECTED_RESOLUTION_HEIGHT : DEFAULT_EXPECTED_RESOLUTION_HEIGHT;
  }

  /**
   * Check if local video is active and producing meaningful data
   */
  private isLocalVideoActive(videoStats: any): boolean {
    if (!videoStats) {
      return false;
    }

    // Check if we're actually sending video packets and have reasonable metrics
    const hasSentPackets = (videoStats.sendPackets || 0) > 0;
    const hasResolution = (videoStats.sendResolutionHeight || 0) > 0 && (videoStats.sendResolutionWidth || 0) > 0;
    const hasFrameRate = (videoStats.sendFrameRate || 0) > 0;

    return hasSentPackets && hasResolution && hasFrameRate;
  }

  /**
   * Check if remote video is active and producing meaningful data
   */
  private isRemoteVideoActive(videoStats: any): boolean {
    if (!videoStats) {
      return false;
    }

    // Check if we're receiving video packets and have reasonable metrics
    const hasReceivedPackets = (videoStats.receivePackets || 0) > 0;
    const hasResolution = (videoStats.receiveResolutionHeight || 0) > 0 && (videoStats.receiveResolutionWidth || 0) > 0;
    const hasFrameRate = (videoStats.receiveFrameRate || 0) > 0;

    return hasReceivedPackets && hasResolution && hasFrameRate;
  }

  private processMeetingQuality(meetingData: any, scores: any): void {
    this.updateMeetingQualityScores(meetingData.videoScore, scores.videoScore);
    this.updateMeetingQualityScores(meetingData.fpsScore, scores.fpsScore);
    this.updateMeetingQualityScores(meetingData.resolutionScore, scores.resolutionScore);
    this.updateMeetingQualityScores(meetingData.receivingMosScore, scores.receivingMosScore);
    this.updateMeetingQualityScores(meetingData.senderMosScore, scores.senderMosScore);
  }

  private updateMeetingQualityScores(valueType: any, valueToCheck: number): void {
    if (valueType.min === 0 || valueType.min > valueToCheck) {
      valueType.min = valueToCheck;
    }

    if (valueType.max === 0 || valueType.max < valueToCheck) {
      valueType.max = valueToCheck;
    }

    valueType.avg += valueToCheck;
  }

  private getVideoQualityScore(
    attendeeId: string,
    MOS: number,
    senderMOS: any,
    fps: number,
    resolutionFrameHeight: number
  ): VideoQualityScore {
    const candidateScores = [];

    // Downstream video MOS
    const receivingMosScore = this.getMOSBasedQualityScore(MOS);
    candidateScores.push(receivingMosScore);

    let senderMosScore = 3;
    if (senderMOS !== undefined && senderMOS !== 'NA') {
      senderMosScore = this.getMOSBasedQualityScore(parseFloat(senderMOS));
      candidateScores.push(senderMosScore);
    }

    const isAttendeeOnStage = this.isAttendeeOnStage(attendeeId);
    const fpsScore = this.getVideoFPSBasedScore(fps, isAttendeeOnStage);
    candidateScores.push(fpsScore);

    const expectedResolutionFrameHeight = this.getExpectedResolutionHeight(!isAttendeeOnStage);

    let resolutionScore = 3;
    if (expectedResolutionFrameHeight) {
      resolutionScore = this.getVideoResolutionBasedScore(
        resolutionFrameHeight,
        expectedResolutionFrameHeight,
        isAttendeeOnStage
      );

      candidateScores.push(resolutionScore);
    }

    const videoScore = Math.min(...candidateScores);
    return {
      videoScore,
      resolutionScore,
      receivingMosScore,
      senderMosScore,
      fpsScore
    };
  }

  private getVideoResolutionBasedScore(
    resolutionFrameHeight: number,
    expectedResolutionFrameHeight: number,
    isAttendeeOnStage: boolean
  ): number {
    if (!isAttendeeOnStage) {
      return QUALITY_SCORE_EXCELLENT;
    }
    let resolutionBasedScore = QUALITY_SCORE_POOR;
    if (
      resolutionFrameHeight === expectedResolutionFrameHeight ||
      resolutionFrameHeight === FALLBACK_RESOLUTION_HEIGHT
    ) {
      resolutionBasedScore = QUALITY_SCORE_EXCELLENT;
    } else if (
      resolutionFrameHeight < expectedResolutionFrameHeight &&
      resolutionFrameHeight >= MIN_GOOD_RESOLUTION_HEIGHT
    ) {
      resolutionBasedScore = QUALITY_SCORE_GOOD;
    }

    return resolutionBasedScore;
  }

  private getVideoFPSBasedScore(fps: number, isAttendeeOnStage: boolean): number {
    if (isAttendeeOnStage) {
      if (fps >= FPS_ONSTAGE_EXCELLENT_THRESHOLD) {
        return QUALITY_SCORE_EXCELLENT;
      } else if (fps >= FPS_ONSTAGE_GOOD_THRESHOLD) {
        return QUALITY_SCORE_GOOD;
      } else {
        return QUALITY_SCORE_POOR;
      }
    } else {
      if (fps >= FPS_OFFSTAGE_EXCELLENT_THRESHOLD) {
        return QUALITY_SCORE_EXCELLENT;
      } else if (fps >= FPS_OFFSTAGE_GOOD_THRESHOLD) {
        return QUALITY_SCORE_GOOD;
      } else {
        return QUALITY_SCORE_POOR;
      }
    }
  }

  private calculateVideoScoreAvgs(meetingData: any, totalCount: number): void {
    if (totalCount > 0) {
      meetingData.videoScore.avg = Math.round((meetingData.videoScore.avg / totalCount) * 100) / 100;
      meetingData.fpsScore.avg = Math.round((meetingData.fpsScore.avg / totalCount) * 100) / 100;
      meetingData.resolutionScore.avg = Math.round((meetingData.resolutionScore.avg / totalCount) * 100) / 100;
      meetingData.receivingMosScore.avg = Math.round((meetingData.receivingMosScore.avg / totalCount) * 100) / 100;
      meetingData.senderMosScore.avg = Math.round((meetingData.senderMosScore.avg / totalCount) * 100) / 100;
    }
  }

  private collectNetworkHealthDataForAllUsers(): Record<string, any> {
    const networkHealthData: Record<string, any> = {};

    // Get all attendee metrics (includes both local and remote users)
    const allMetrics = this.studioMetrics.getAllMetrics();
    const remoteUsers = this.client.remoteUsers;

    // Calculate numVideos once outside the loop (same for all attendees)
    const localVideoTrack = this.client.localTracks?.find(track => track.trackMediaType === 'video');
    const localHasVideo = !!localVideoTrack;
    const numVideos = remoteUsers.filter(user => user.hasVideo).length + (localHasVideo ? 1 : 0);

    for (const attendeeId in allMetrics) {
      const attendeeMetrics = allMetrics[attendeeId];

      if (!attendeeMetrics) {
        continue;
      }
      const isLocalUser = attendeeId === this.currentAttendeeId;

      // For remote users, get their state from remoteUsers
      // For local user, get state from client.localTracks
      let hasAudio = false;
      let hasVideo = false;
      let audioMuted = false;
      let videoMuted = false;

      if (isLocalUser) {
        // Get local audio/video state from client.localTracks
        const localAudioTrack = this.client.localTracks?.find(track => track.trackMediaType === 'audio');
        const localVideoTrack = this.client.localTracks?.find(track => track.trackMediaType === 'video');

        hasAudio = !!localAudioTrack;
        hasVideo = !!localVideoTrack;
        audioMuted = localAudioTrack ? !localAudioTrack.enabled : true;
        videoMuted = localVideoTrack ? !localVideoTrack.enabled : true;
      } else {
        // Get remote user state from remoteUsers
        const remoteUser = remoteUsers.find(user => user.uid.toString() === attendeeId);
        if (remoteUser) {
          hasAudio = remoteUser.hasAudio;
          hasVideo = remoteUser.hasVideo;
          audioMuted = !remoteUser.hasAudio;
          videoMuted = !remoteUser.hasVideo;
        }
      }

      networkHealthData[attendeeId] = {
        // Network quality and MOS scores
        networkQuality: attendeeMetrics.getMetric('networkQuality') || 'unknown',
        videoMOS: attendeeMetrics.getMetric('videoMOS') || 0,

        // Video frame height
        videoFrameHeight:
          attendeeMetrics.getMetric('videoUpstreamFrameHeight') ||
          attendeeMetrics.getMetric('videoDownstreamFrameHeight') ||
          0,
        expectedResolutionHeight: this.getExpectedResolutionHeight(false),

        // Video metrics
        videoPacketLoss:
          attendeeMetrics.getMetric('videoUpstreamPacketLossPercent') ||
          attendeeMetrics.getMetric('videoDownstreamPacketLossPercent') ||
          0,
        videoJitter:
          attendeeMetrics.getMetric('videoUpstreamJitterMs') ||
          attendeeMetrics.getMetric('videoDownstreamJitterMs') ||
          0,
        videoLatency:
          attendeeMetrics.getMetric('videoUpstreamRoundTripTimeMs') ||
          attendeeMetrics.getMetric('videoDownstreamRoundTripTimeMs') ||
          0,

        // Video frame info
        videoFrame: this.formatVideoFrame(attendeeMetrics, isLocalUser),
        receivedFrame: this.formatReceivedFrame(attendeeMetrics),

        // Audio metrics
        audioPacketLoss:
          attendeeMetrics.getMetric('audioPacketLossPercent') ||
          attendeeMetrics.getMetric('audioPacketsReceivedFractionLoss') ||
          0,
        audioJitter:
          attendeeMetrics.getMetric('audioUpstreamJitterMs') ||
          attendeeMetrics.getMetric('audioDownstreamJitterMs') ||
          0,
        audioLatency: attendeeMetrics.getMetric('audioUpstreamRoundTripTimeMs') || 0,

        // Delay metrics
        audioDelay: attendeeMetrics.getMetric('audioDelay') || 0,
        videoDelay: attendeeMetrics.getMetric('videoDelay') || 0,
        transportDelay: attendeeMetrics.getMetric('transportDelay') || 0,
        end2EndDelay: attendeeMetrics.getMetric('end2EndDelay') || 0,
        receiveDelay: attendeeMetrics.getMetric('receiveDelay') || 0,
        encodeDelay: attendeeMetrics.getMetric('encodeDelay') || 0,

        // Freeze rate metrics
        audioFreezeRate: attendeeMetrics.getMetric('audioFreezeRate') || 0,
        videoFreezeRate: attendeeMetrics.getMetric('videoFreezeRate') || 0,
        audioFreezeTotalTime: attendeeMetrics.getMetric('audioFreezeTotalTime') || 0,
        videoFreezeTotalTime: attendeeMetrics.getMetric('videoFreezeTotalTime') || 0,

        // Bandwidth info
        uploadBandwidth:
          (attendeeMetrics.getMetric('videoUpstreamBitrate') || 0) / 1000 + // Convert to kbps
          (attendeeMetrics.getMetric('availableOutgoingBitrate') || 0) / 1000,
        downloadBandwidth:
          (attendeeMetrics.getMetric('videoDownstreamBitrate') || 0) / 1000 + // Convert to kbps
          (attendeeMetrics.getMetric('availableIncomingBitrate') || 0) / 1000,

        // Muted states
        audioMuted,
        videoMuted,

        // Additional states
        hasAudio,
        hasVideo,
        isPageHidden: document?.hidden,
        isLocalUser: isLocalUser,

        // Additional metrics for dashboard compatibility
        audioUpstreamLevel: attendeeMetrics.getMetric('audioUpstreamLevel') || 0,
        audioPacketsSent: attendeeMetrics.getMetric('audioPacketsSent') || 0,
        audioDownstreamLevel: attendeeMetrics.getMetric('receiveLevel') || 0,
        audioDownstreamJitterMs: attendeeMetrics.getMetric('audioDownstreamJitterMs') || 0,
        audioPacketsReceived: attendeeMetrics.getMetric('audioPacketsReceived') || 0,
        audioPacketsReceivedFractionLoss: attendeeMetrics.getMetric('audioPacketsReceivedFractionLoss') || 0,
        videoUpstreamFrameHeight: attendeeMetrics.getMetric('videoUpstreamFrameHeight') || 0,
        videoUpstreamFrameWidth: attendeeMetrics.getMetric('videoUpstreamFrameWidth') || 0,
        videoUpstreamBitrate: attendeeMetrics.getMetric('videoUpstreamBitrate') || 0,
        videoUpstreamFramesEncodedPerSecond: attendeeMetrics.getMetric('videoUpstreamFramesEncodedPerSecond') || 0,
        videoDownstreamBitrate: attendeeMetrics.getMetric('videoDownstreamBitrate') || 0,
        codec: attendeeMetrics.getMetric('codec') || 'unknown',
        availableIncomingBitrate: attendeeMetrics.getMetric('availableIncomingBitrate') || 0,
        availableOutgoingBitrate: attendeeMetrics.getMetric('availableOutgoingBitrate') || 0,

        // Media source metrics
        mediaSourceHeight: attendeeMetrics.getMetric('captureResolutionHeight') || 0,
        mediaSourceWidth: attendeeMetrics.getMetric('captureResolutionWidth') || 0,
        mediaSourceFramesPerSecond: attendeeMetrics.getMetric('captureFrameRate') || 0,

        // Video packet metrics
        videoPacketReceivedPerSecond: attendeeMetrics.getMetric('videoDownstreamPacketsReceived') || 0,
        videoPacketSentPerSecond: attendeeMetrics.getMetric('videoUpstreamPacketsSent') || 0,
        videoUpstreamPacketsSent: attendeeMetrics.getMetric('videoUpstreamPacketsSent') || 0,
        nackCountReceivedPerSecond: attendeeMetrics.getMetric('nackCountReceivedPerSecond') || 0,

        // Video codec/quality metrics
        videoDownstreamTotalDecodeTimePerSecond:
          attendeeMetrics.getMetric('videoDownstreamTotalDecodeTimePerSecond') || 0,

        // Participant metrics
        numVideos
      };
    }

    // Also include any additional network health data from remoteAttendeeNetworkHealth
    if (this.remoteAttendeeNetworkHealth && Object.keys(this.remoteAttendeeNetworkHealth).length > 0) {
      for (const attendeeId in this.remoteAttendeeNetworkHealth) {
        networkHealthData[attendeeId] = {
          ...networkHealthData[attendeeId],
          ...this.remoteAttendeeNetworkHealth[attendeeId]
        };
      }
    }

    return networkHealthData;
  }

  private formatVideoFrame(attendeeMetrics: AttendeeMetrics, isLocalUser: boolean): string {
    const frameRate = isLocalUser
      ? attendeeMetrics.getMetric('videoUpstreamFramesEncodedPerSecond') || 0
      : attendeeMetrics.getMetric('receiveFrameRate') || 0;
    const width = isLocalUser
      ? attendeeMetrics.getMetric('videoUpstreamFrameWidth') || 0
      : attendeeMetrics.getMetric('videoDownstreamFrameWidth') || 0;
    const height = isLocalUser
      ? attendeeMetrics.getMetric('videoUpstreamFrameHeight') || 0
      : attendeeMetrics.getMetric('videoDownstreamFrameHeight') || 0;

    if (frameRate === 0 && width === 0 && height === 0) {
      return 'NA';
    }
    return `${width}x${height}, ${frameRate}fps`;
  }

  private formatReceivedFrame(attendeeMetrics: AttendeeMetrics): string {
    const frameRate = attendeeMetrics.getMetric('receiveFrameRate') || 0;
    const width = attendeeMetrics.getMetric('videoDownstreamFrameWidth') || 0;
    const height = attendeeMetrics.getMetric('videoDownstreamFrameHeight') || 0;

    if (frameRate === 0 && width === 0 && height === 0) {
      return 'NA';
    }
    return `${width}x${height}, ${frameRate}fps`;
  }

  updateOnstageAttendees(onstageAttendees: Record<string, any>): void {
    this.onstageAttendees = onstageAttendees;
  }

  /**
   * Process local attendee metrics (upstream data) - refactored for clarity
   */
  private processLocalAttendeeMetrics(): void {
    try {
      const currentAttendeeMetrics = this.studioMetrics.getAttendeeMetrics(this.currentAttendeeId);
      const audioMetrics = this.client.getLocalAudioStats();
      const videoMetrics = this.client.getLocalVideoStats();

      // Process local audio stats
      if (audioMetrics) {
        this.updateLocalAudioMetrics(currentAttendeeMetrics, audioMetrics);
      }

      // Process local video stats
      if (videoMetrics) {
        this.updateLocalVideoMetrics(currentAttendeeMetrics, videoMetrics);
      }

      this.studioMetrics.updateAttendeeMetrics(this.currentAttendeeId, currentAttendeeMetrics);
    } catch (error) {
      LOG.error('Error processing local attendee metrics:', error);
    }
  }

  /**
   * Update local audio metrics
   */
  private updateLocalAudioMetrics(currentAttendeeMetrics: AttendeeMetrics, audioMetrics: any): void {
    currentAttendeeMetrics.updateMetric('audioPacketsSent', audioMetrics.sendPackets || 0);
    currentAttendeeMetrics.updateMetric('audioPacketLossPercent', audioMetrics.currentPacketLossRate || 0);
    currentAttendeeMetrics.updateMetric('audioUpstreamRoundTripTimeMs', audioMetrics.sendRttMs || 0);
    currentAttendeeMetrics.updateMetric('audioUpstreamJitterMs', audioMetrics.sendJitterMs || 0);
    currentAttendeeMetrics.updateMetric('availableOutgoingBitrate', audioMetrics.sendBitrate || 0);
    currentAttendeeMetrics.updateMetric('audioUpstreamLevel', audioMetrics.sendVolumeLevel || 0);
    // Additional metrics from sample data
    currentAttendeeMetrics.updateMetric('audioCodec', audioMetrics.codecType || DEFAULT_CODEC);
    currentAttendeeMetrics.updateMetric('currentPacketLossRate', audioMetrics.currentPacketLossRate || 0);
  }

  /**
   * Update local video metrics
   */
  private updateLocalVideoMetrics(currentAttendeeMetrics: AttendeeMetrics, videoMetrics: any): void {
    currentAttendeeMetrics.updateMetric('videoUpstreamBitrate', videoMetrics.sendBitrate || 0);
    currentAttendeeMetrics.updateMetric('videoUpstreamPacketsSent', videoMetrics.sendPackets || 0);
    currentAttendeeMetrics.updateMetric('videoUpstreamPacketLossPercent', videoMetrics.currentPacketLossRate || 0);
    currentAttendeeMetrics.updateMetric('videoUpstreamFrameHeight', videoMetrics.sendResolutionHeight || 0);
    currentAttendeeMetrics.updateMetric('videoUpstreamFrameWidth', videoMetrics.sendResolutionWidth || 0);
    currentAttendeeMetrics.updateMetric('videoUpstreamJitterMs', videoMetrics.sendJitterMs || 0);
    currentAttendeeMetrics.updateMetric('videoUpstreamRoundTripTimeMs', videoMetrics.sendRttMs || 0);
    currentAttendeeMetrics.updateMetric('videoUpstreamFramesEncodedPerSecond', videoMetrics.sendFrameRate || 0);
    currentAttendeeMetrics.updateMetric('codec', videoMetrics.codecType || DEFAULT_CODEC);

    const MOSScore = this.computeMOS(
      videoMetrics.currentPacketLossRate || 0,
      videoMetrics.sendJitterMs || 0,
      videoMetrics.sendRttMs || 0
    );

    currentAttendeeMetrics.updateMetric('videoMOS', MOSScore);

    // Additional metrics available in Agora SDK (based on actual sample data)
    currentAttendeeMetrics.updateMetric('videoFreezeTotalTime', videoMetrics.totalFreezeTime || 0);
    currentAttendeeMetrics.updateMetric('captureFrameRate', videoMetrics.captureFrameRate || 0);
    currentAttendeeMetrics.updateMetric('encodeDelay', videoMetrics.encodeDelay || 0);
    currentAttendeeMetrics.updateMetric('targetSendBitrate', videoMetrics.targetSendBitrate || 0);
    currentAttendeeMetrics.updateMetric('totalDuration', videoMetrics.totalDuration || 0);

    // Frame metrics using actual properties
    currentAttendeeMetrics.updateMetric('framesSent', videoMetrics.sendPackets || 0); // sendPackets represents frames sent
    currentAttendeeMetrics.updateMetric('captureResolutionHeight', videoMetrics.captureResolutionHeight || 0);
    currentAttendeeMetrics.updateMetric('captureResolutionWidth', videoMetrics.captureResolutionWidth || 0);
  }

  /**
   * Process remote attendees metrics (downstream data) - refactored into smaller methods
   */
  private processRemoteAttendeesMetrics(): void {
    const remoteAudioStats = this.client.getRemoteAudioStats();
    const remoteVideoStats = this.client.getRemoteVideoStats();

    // Process remote audio stats
    if (remoteAudioStats) {
      this.processRemoteAudioStats(remoteAudioStats);
    }

    // Process remote video stats
    if (remoteVideoStats) {
      this.processRemoteVideoStats(remoteVideoStats);
    }
  }

  /**
   * Process remote attendee audio stats
   */
  private processRemoteAudioStats(remoteAudioStats: any): void {
    for (const attendeeId in remoteAudioStats) {
      const attendeeMetric = this.studioMetrics.getAttendeeMetrics(attendeeId);
      const audioStat = remoteAudioStats[attendeeId];

      if (audioStat) {
        this.updateAudioMetrics(attendeeMetric, audioStat);
        this.studioMetrics.updateAttendeeMetrics(attendeeId, attendeeMetric);
      }
    }
  }

  /**
   * Update audio metrics for an attendee
   */
  private updateAudioMetrics(attendeeMetric: AttendeeMetrics, audioStat: any): void {
    attendeeMetric.updateMetric('audioPacketsReceived', audioStat.receivePackets || 0);
    attendeeMetric.updateMetric('audioPacketsReceivedFractionLoss', audioStat.packetLossRate || 0);
    attendeeMetric.updateMetric('audioDownstreamJitterMs', audioStat.freezeRate || 0);
    attendeeMetric.updateMetric('availableIncomingBitrate', audioStat.receiveBitrate || 0);
    attendeeMetric.updateMetric('receiveDelay', audioStat.receiveDelay || 0);

    // Additional delay and freeze metrics available in Agora (based on sample data)
    attendeeMetric.updateMetric('audioDelay', audioStat.receiveDelay || 0);
    attendeeMetric.updateMetric('transportDelay', audioStat.transportDelay || 0);
    attendeeMetric.updateMetric('end2EndDelay', audioStat.end2EndDelay || 0);
    attendeeMetric.updateMetric('audioFreezeRate', audioStat.freezeRate || 0);
    attendeeMetric.updateMetric('audioFreezeTotalTime', audioStat.totalFreezeTime || 0);
    attendeeMetric.updateMetric('receiveLevel', audioStat.receiveLevel || 0);
    attendeeMetric.updateMetric('receiveBytes', audioStat.receiveBytes || 0);
    attendeeMetric.updateMetric('totalDuration', audioStat.totalDuration || 0);
    attendeeMetric.updateMetric('publishDuration', audioStat.publishDuration || 0);
    attendeeMetric.updateMetric('currentPacketLossRate', audioStat.currentPacketLossRate || 0);
  }

  /**
   * Process remote attendee video stats
   */
  private processRemoteVideoStats(remoteVideoStats: any): void {
    for (const attendeeId in remoteVideoStats) {
      const attendeeMetric = this.studioMetrics.getAttendeeMetrics(attendeeId);
      const videoMetrics = remoteVideoStats[attendeeId];

      if (videoMetrics) {
        this.updateVideoMetrics(attendeeMetric, videoMetrics);
        this.updateVideoQualityMetrics(attendeeMetric, videoMetrics, attendeeId);
        this.studioMetrics.updateAttendeeMetrics(attendeeId, attendeeMetric);
      }
    }
  }

  /**
   * Update video metrics for an attendee
   */
  private updateVideoMetrics(attendeeMetric: AttendeeMetrics, videoMetrics: any): void {
    attendeeMetric.updateMetric('videoDownstreamBitrate', videoMetrics.receiveBitrate || 0);
    attendeeMetric.updateMetric('videoDownstreamPacketsReceived', videoMetrics.receivePackets || 0);
    attendeeMetric.updateMetric('videoDownstreamPacketLossPercent', videoMetrics.packetLossRate || 0);
    attendeeMetric.updateMetric('videoDownstreamFrameHeight', videoMetrics.receiveResolutionHeight || 0);
    attendeeMetric.updateMetric('videoDownstreamFrameWidth', videoMetrics.receiveResolutionWidth || 0);
    attendeeMetric.updateMetric('videoDownstreamJitterMs', videoMetrics.end2EndDelay || 0);
    attendeeMetric.updateMetric('videoDownstreamRoundTripTimeMs', videoMetrics.end2EndDelay || 0);
    attendeeMetric.updateMetric('videoDownstreamFramesDecodedPerSecond', videoMetrics.decodeFrameRate || 0);
    attendeeMetric.updateMetric('codec', videoMetrics.codecType || DEFAULT_CODEC);

    // Additional video metrics that are actually available in Agora (based on sample data)
    attendeeMetric.updateMetric('transportDelay', videoMetrics.transportDelay || 0);
    attendeeMetric.updateMetric('end2EndDelay', videoMetrics.end2EndDelay || 0);
    attendeeMetric.updateMetric('videoDelay', videoMetrics.end2EndDelay || 0);
    attendeeMetric.updateMetric('receiveDelay', videoMetrics.receiveDelay || 0);

    // Freeze-related metrics (confirmed available in sample data)
    attendeeMetric.updateMetric('videoFreezeRate', videoMetrics.freezeRate || 0);
    attendeeMetric.updateMetric('videoFreezeTotalTime', videoMetrics.totalFreezeTime || 0);

    // Frame rate metrics (confirmed available in sample data)
    attendeeMetric.updateMetric('videoRenderFrameRate', videoMetrics.renderFrameRate || 0);
    attendeeMetric.updateMetric('receiveFrameRate', videoMetrics.receiveFrameRate || 0);
    attendeeMetric.updateMetric('videoDownstreamFramesDecodedPerSecond', videoMetrics.decodeFrameRate || 0);

    // Duration and bytes metrics
    attendeeMetric.updateMetric('totalDuration', videoMetrics.totalDuration || 0);
    attendeeMetric.updateMetric('receiveBytes', videoMetrics.receiveBytes || 0);
    attendeeMetric.updateMetric('publishDuration', videoMetrics.publishDuration || 0);
  }

  /**
   * Update video quality metrics and network health for an attendee
   */
  private updateVideoQualityMetrics(attendeeMetric: AttendeeMetrics, videoMetrics: any, attendeeId: string): void {
    const MOSScore = this.computeMOS(videoMetrics.packetLossRate || 0, 0, videoMetrics.end2EndDelay || 0);

    const networkQualityResult = this.getAgoraNetworkQuality(attendeeId);
    const networkQuality =
      networkQualityResult.qualityString !== 'unknown'
        ? networkQualityResult.qualityString
        : this.getMOSBasedNetworkQuality(MOSScore, videoMetrics.receiveResolutionHeight);

    attendeeMetric.updateMetric('networkQuality', networkQuality);
    attendeeMetric.updateMetric('videoMOS', MOSScore);
  }

  /**
   * MOS (Mean Opinion Score) calculation using constants
   */
  private computeMOS(packetLoss: number = 0, jitter: number = 0, RTT: number = 0): number {
    if (!packetLoss) {
      packetLoss = 0;
    }
    if (!jitter) {
      jitter = 0;
    }
    if (!RTT) {
      RTT = 0;
    }

    const effectiveLatency = RTT + jitter * MOS_JITTER_MULTIPLIER + MOS_BASE_LATENCY_OFFSET;
    let rFactor = MOS_BASE_R_FACTOR - effectiveLatency / MOS_LATENCY_FACTOR_NORMAL;
    if (effectiveLatency >= MOS_HIGH_LATENCY_THRESHOLD) {
      rFactor = MOS_BASE_R_FACTOR - effectiveLatency / MOS_LATENCY_FACTOR_HIGH - MOS_HIGH_LATENCY_PENALTY;
    }
    rFactor -= packetLoss * MOS_PACKET_LOSS_MULTIPLIER;
    if (rFactor < 0) {
      rFactor = 0;
    }
    const MOSScore =
      MOS_CALCULATION_CONSTANT_A +
      MOS_CALCULATION_CONSTANT_B * rFactor +
      MOS_CALCULATION_CONSTANT_C *
        rFactor *
        (rFactor - MOS_CALCULATION_R_FACTOR_OFFSET) *
        (MOS_CALCULATION_MAX_R_FACTOR - rFactor);
    return Math.round(MOSScore * 100) / 100;
  }

  /**
   * Get Agora network quality using actual Agora API
   */
  private getAgoraNetworkQuality(attendeeId?: string): NetworkQualityResult {
    try {
      let networkQuality: NetworkQuality | null = null;

      if (attendeeId) {
        const remoteQualities = this.client.getRemoteNetworkQuality();
        networkQuality = remoteQualities[attendeeId] || null;
      }

      if (networkQuality) {
        return {
          qualityString: AGORA_NETWORK_QUALITY_TO_STRING_MAP[networkQuality.uplinkNetworkQuality] || 'unknown',
          agoraQuality: networkQuality
        };
      }

      return {
        qualityString: 'unknown',
        agoraQuality: null
      };
    } catch (error) {
      LOG.error('Error getting Agora network quality:', error);
      return {
        qualityString: 'unknown',
        agoraQuality: null
      };
    }
  }

  /**
   * Get network quality based on MOS score and resolution
   */
  private getMOSBasedNetworkQuality(
    MOS: number,
    resolutionFrameHeight?: number,
    expectedResolutionFrameHeight?: number
  ): string {
    let MOSBasedScore = QUALITY_SCORE_POOR;
    if (MOS >= MOS_EXCELLENT_THRESHOLD) {
      MOSBasedScore = QUALITY_SCORE_EXCELLENT;
    } else if (MOS >= MOS_GOOD_THRESHOLD) {
      MOSBasedScore = QUALITY_SCORE_GOOD;
    }

    if (expectedResolutionFrameHeight && resolutionFrameHeight) {
      let resolutionBasedScore = QUALITY_SCORE_POOR;
      if (
        resolutionFrameHeight === expectedResolutionFrameHeight ||
        resolutionFrameHeight === FALLBACK_RESOLUTION_HEIGHT
      ) {
        resolutionBasedScore = QUALITY_SCORE_EXCELLENT;
      } else if (
        resolutionFrameHeight < expectedResolutionFrameHeight &&
        resolutionFrameHeight > FALLBACK_RESOLUTION_HEIGHT
      ) {
        resolutionBasedScore = QUALITY_SCORE_GOOD;
      }

      const finalScore = Math.min(MOSBasedScore, resolutionBasedScore);
      return SCORE_QUALITY_MAP[finalScore as keyof typeof SCORE_QUALITY_MAP];
    } else {
      return SCORE_QUALITY_MAP[MOSBasedScore as keyof typeof SCORE_QUALITY_MAP];
    }
  }

  stop(): void {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
      LOG.info('AgoraMetricsProcessor stopped');
    }
  }

  destroy(): void {
    this.stop();
    this.resetQualityTracking();
    this.studioMetrics = new StudioMetrics();

    if (AgoraMetricsProcessor.instance === this) {
      AgoraMetricsProcessor.instance = null;
    }

    LOG.info('AgoraMetricsProcessor destroyed');
  }

  // Public method to get current quality status
  getQualityStatus(): QualityStatus {
    return {
      audioSamples: this.audioQuality.length,
      videoSamples: Object.keys(this.videoQuality).length,
      totalSamples: this.audioQuality.length
    };
  }

  /**
   * Get all metrics in raw format
   */
  getMetrics(): { [attendeeId: string]: AttendeeMetrics } {
    this.processLocalAttendeeMetrics();
    this.processRemoteAttendeesMetrics();
    return this.studioMetrics.getAllMetrics();
  }

  /**
   * Set processing interval
   */
  setProcessingInterval(interval: number): void {
    this.intervalMs = interval;
    // Stop existing interval and restart with new interval
    this.stop();
    this.startProcessing();
  }
}

export default AgoraMetricsProcessor;
